import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { TenantResponse } from 'api/types';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

// ============================================================================
// TYPES
// ============================================================================

interface TenantViewModalProps {
  isOpen: boolean;
  tenant: TenantResponse | null;
  onClose: () => void;
  onEdit: (tenant: TenantResponse) => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (tenantCode: string) => void;
}

// ============================================================================
// HELPERS
// ============================================================================

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
    <div style={{ width: '180px', flexShrink: 0, fontWeight: 500, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
      {label}
    </div>
    <div style={{
      flex: 1,
      fontSize: '0.85rem',
      color: 'var(--text-primary)',
      background: 'var(--bg-secondary)',
      border: '1px solid var(--form-border-color)',
      borderRadius: '0.375rem',
      padding: '0.375rem 0.625rem',
      minHeight: '2rem',
      display: 'flex',
      alignItems: 'center',
    }}>
      {value || '-'}
    </div>
  </div>
);

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.4rem 0.75rem',
    background: 'var(--bg-secondary)',
    borderLeft: '3px solid var(--primary)',
    borderRadius: '0 0.25rem 0.25rem 0',
    marginBottom: '0.75rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
  }}>
    {icon}
    {title}
  </div>
);

const StatusBadge: React.FC<{ tenant: TenantResponse }> = ({ tenant }) => {
  if (tenant.isSuspended) return <span className="badge badge-suspended">Suspended</span>;
  if (tenant.isTrial) return <span className="badge badge-warning">Trial</span>;
  if (tenant.isActive) return <span className="badge badge-active">Active</span>;
  return <span className="badge badge-inactive">{tenant.tenantStatusName || 'Unknown'}</span>;
};

// ============================================================================
// COMPONENT
// ============================================================================

const TenantViewModal: React.FC<TenantViewModalProps> = ({
  isOpen,
  tenant,
  onClose,
  onEdit,
  tabId,
  moduleName,
  onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !tenant) return;
    minimize({
      id: `${tabId}-view-modal-${tenant.tenantCode}`,
      title: `View - ${tenant.tenantCode}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(tenant.tenantCode),
    });
    onClose();
  }, [tabId, moduleName, tenant, minimize, onClose, onRestoreView]);

  if (!isOpen || !tenant) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg">
        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">
            <IconSystem.home size={20} />
            Tenant Details
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="modal-content">
          {/* Identity Section */}
          <div style={{ marginBottom: '1.5rem' }}>
            <SectionHeader icon={<IconSystem.info size={16} />} title="Identity" />
            <DetailRow label="Code" value={tenant.tenantCode} />
            <DetailRow label="Name" value={tenant.tenantName} />
            <DetailRow label="Display Name" value={tenant.tenantDisplayName} />
            <DetailRow label="Description" value={tenant.tenantDescription} />
            <DetailRow label="Category" value={
              <span className={`badge ${tenant.tenantCategory === 'internal' ? 'badge-info' : 'badge-default'}`}>
                {tenant.tenantCategory}
              </span>
            } />
            <DetailRow label="Status" value={<StatusBadge tenant={tenant} />} />
          </div>

          {/* Configuration Section */}
          <div style={{ marginBottom: '1.5rem' }}>
            <SectionHeader icon={<IconSystem.settings size={16} />} title="Configuration" />
            <DetailRow label="Realm Type" value={tenant.keycloakRealmType} />
            <DetailRow label="Subscription Plan" value={tenant.subscriptionPlan} />
            <DetailRow label="Max Users" value={tenant.maxUsers ? tenant.maxUsers.toLocaleString() : undefined} />
            <DetailRow label="Max Storage (GB)" value={tenant.maxStorageGb ? tenant.maxStorageGb.toLocaleString() : undefined} />
            <DetailRow label="Trial" value={tenant.isTrial ? 'Yes' : 'No'} />
            {tenant.isTrial && tenant.trialEndDate && (
              <DetailRow label="Trial End Date" value={new Date(tenant.trialEndDate).toLocaleDateString()} />
            )}
          </div>

          {/* Statistics Section */}
          <div style={{ marginBottom: '1.5rem' }}>
            <SectionHeader icon={<IconSystem.chart size={16} />} title="Statistics" />
            <div className="form-grid-3">
              <div style={{
                background: 'var(--bg-secondary)',
                borderRadius: '0.5rem',
                padding: '1rem',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>
                  {tenant.totalUserCount ?? '-'}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Total Users</div>
              </div>
              <div style={{
                background: 'var(--bg-secondary)',
                borderRadius: '0.5rem',
                padding: '1rem',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>
                  {tenant.adminUserCount ?? '-'}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Admin Users</div>
              </div>
              <div style={{
                background: 'var(--bg-secondary)',
                borderRadius: '0.5rem',
                padding: '1rem',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>
                  {tenant.totalApplications ?? '-'}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Applications</div>
              </div>
            </div>
          </div>

          {/* Dates Section */}
          <div>
            <SectionHeader icon={<IconSystem.calendar size={16} />} title="Dates" />
            <DetailRow label="Onboarding Date" value={tenant.onboardingDate ? new Date(tenant.onboardingDate).toLocaleDateString() : undefined} />
            <DetailRow label="Last Activity" value={tenant.lastActivityDate ? new Date(tenant.lastActivityDate).toLocaleString() : undefined} />
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => {
              const tid = (tenant as any).tenantId ?? (tenant as any).id;
              window.location.href = `/Layout/main_layout?module=tenant-app-assignment&tenantId=${tid}`;
            }}
            title="Open the Application Assignments screen pre-filtered to this tenant"
          >
            <IconSystem.link size={16} style={{ marginRight: '0.25rem' }} />
            View Application Assignments
          </button>
          <button className="btn btn-primary" onClick={() => onEdit(tenant)}>
            <IconSystem.edit size={16} style={{ marginRight: '0.25rem' }} />
            Edit
          </button>
        </div>
      </div>
    </>
  );
};

export default TenantViewModal;
